// ═══════════════════════════════════════════════════════════════════════════
//  ΛΙΣΤΑ EARLY CHECK-OUT — ΚΟΙΝΗ ΣΥΝΑΡΤΗΣΗ ΤΑΞΙΝΟΜΗΣΗΣ (prod + v2)
//  ---------------------------------------------------------------------------
//  Σειρά (ΤΙΠΟΤΑ δεν κρύβεται):
//    (α) ΣΕ ΕΞΕΛΙΞΗ  = check-in έγινε, check-out ΟΧΙ → πρώτος αυτός που λήγει πιο κοντά
//    (β) ΤΡΕΧΟΥΣΑ ΕΒΔΟΜΑΔΑ = λήγουν έως και αυτό το Σάββατο → αύξουσα λήξη
//    (γ) ΠΡΟΗΓΟΥΜΕΝΟΙ = έληξαν → φθίνουσα (πρόσφατοι → παλιότεροι), όλα τα έτη
//    (δ) ΜΕΛΛΟΝΤΙΚΟΙ  = λήγουν μετά → αύξουσα, στο τέλος
//  Ίδια λογική για Early Check-IN: πέρασε `mode:'in'` (μετράει τις ημ. έναρξης).
//  ΚΑΘΑΡΗ συνάρτηση: κάθε app δίνει τα δικά της bookings + statusOf (από τον δικό
//  της μηχανισμό — v2 ciStates, prod pending list). Καμία εξάρτηση από React.
// ═══════════════════════════════════════════════════════════════════════════

export type CiStatus = { checkInDone: boolean; checkOutDone: boolean };
export type EcBucket = 'progress' | 'week' | 'past' | 'future';
export interface EcRow { b: any; bucket: EcBucket; key: string }

const ymd = (s: any): string => String(s || '').slice(0, 10);
export const ecEnd = (b: any): string => ymd(b?.endDate || b?.checkOutDate);
export const ecStart = (b: any): string => ymd(b?.startDate || b?.checkInDate);

/** Ώρα Αθήνας «σήμερα» ΧΩΡΙΣ εξάρτηση (τα δύο apps διαφέρουν στο charterWindow). */
export function ecAthensToday(now: Date = new Date()): string {
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens',
      year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const g = (t: string) => p.find(x => x.type === t)?.value || '';
    return `${g('year')}-${g('month')}-${g('day')}`;
  } catch { return now.toISOString().slice(0, 10); }
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const toYmd = (dt: Date) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;

/** Το ΕΡΧΟΜΕΝΟ Σάββατο (αν σήμερα είναι Σάββατο → σήμερα), ως YYYY-MM-DD. */
export function ecComingSaturday(todayYmd: string): string {
  const [y, m, d] = todayYmd.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + ((6 - dt.getDay() + 7) % 7));   // 6 = Σάββατο
  return toYmd(dt);
}

/** Η ΔΕΥΤΕΡΑ της τρέχουσας εβδομάδας (αρχή εβδομάδας), ως YYYY-MM-DD. */
export function ecWeekMonday(todayYmd: string): string {
  const [y, m, d] = todayYmd.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));       // 1 = Δευτέρα
  return toYmd(dt);
}

export function orderEarlyCheckout(
  bookings: any[],
  statusOf: (b: any) => CiStatus,
  opts: { todayYmd?: string; mode?: 'in' | 'out' } = {},
): EcRow[] {
  const today = opts.todayYmd || ecAthensToday();
  const sat = ecComingSaturday(today);
  const mon = ecWeekMonday(today);
  const dateOf = opts.mode === 'in' ? ecStart : ecEnd;
  const progress: EcRow[] = [], week: EcRow[] = [], past: EcRow[] = [], future: EcRow[] = [];

  for (const b of bookings || []) {
    const key = dateOf(b);
    const st = statusOf(b) || { checkInDone: false, checkOutDone: false };
    /* (α) ΣΕ ΕΞΕΛΙΞΗ = το σκάφος είναι ΕΞΩ ΤΩΡΑ: check-in έγινε, check-out όχι, και
       δεν έχει λήξει ακόμη (λήξη σήμερα ή αργότερα). Παλιοί ναύλοι που δεν έκαναν
       ποτέ check-out ΔΕΝ είναι «σε εξέλιξη» — πάνε στους προηγούμενους. */
    if (st.checkInDone && !st.checkOutDone && (key === '' || key >= today)) {
      progress.push({ b, bucket: 'progress', key }); continue;
    }
    if (!key) { future.push({ b, bucket: 'future', key }); continue; }   // χωρίς ημερομηνία → τέλος
    // (β) ΤΡΕΧΟΥΣΑ ΕΒΔΟΜΑΔΑ: λήξη μέσα στην εβδομάδα (Δευτέρα…Σάββατο), ό,τι δεν μπήκε στο (α)
    if (key >= mon && key <= sat) { week.push({ b, bucket: 'week', key }); continue; }
    if (key < mon) { past.push({ b, bucket: 'past', key }); continue; }  // (γ) προηγούμενοι (πριν αυτή την εβδομάδα)
    future.push({ b, bucket: 'future', key });                           // (δ) μελλοντικοί (μετά το Σάββατο)
  }
  progress.sort((x, y) => x.key.localeCompare(y.key));   // πιο κοντινή λήξη πρώτη
  week.sort((x, y) => x.key.localeCompare(y.key));       // αύξουσα
  past.sort((x, y) => y.key.localeCompare(x.key));       // φθίνουσα (πρόσφατοι πρώτοι)
  future.sort((x, y) => x.key.localeCompare(y.key));     // αύξουσα
  return [...progress, ...week, ...past, ...future];
}

/** Ελαφρύ χρωματιστό φόντο: «σε εξέλιξη» + «τρέχουσα εβδομάδα». */
export function ecRowBg(bucket: EcBucket): string | undefined {
  if (bucket === 'progress') return '#fef9c3';   // ανοιχτό κίτρινο — ζει τώρα
  if (bucket === 'week')     return '#e0f2fe';   // ανοιχτό γαλάζιο — αυτή την εβδομάδα
  return undefined;
}

/** Φίλτρο αναζήτησης: αριθμός / σκάφος / όνομα (πεζά, καθώς γράφεις). */
export function ecMatches(b: any, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [
    b?.booking_number, b?.bookingNumber, b?.vesselName, b?.boatName,
    b?.chartererFirstName, b?.chartererLastName, b?.skipperFirstName, b?.skipperLastName,
  ].map(x => String(x || '').toLowerCase()).join(' ');
  return s.split(/\s+/).every(w => hay.includes(w));
}
